import { useAppSelector } from '@/store/hooks'
import type { UserWorkspace } from '@/store/auth/authSlice'
import Modal from '@/components/modals/Modal/Modal'
import WorkspaceAvatar from '@/components/ui/WorkspaceAvatar/WorkspaceAvatar'
import { TextH2, TextNorm } from '@/components/ui/Text'
import css from './SelectWorkspaceModal.module.css'

interface SelectWorkspaceModalProps {
    canClose: boolean
    onClose: () => void
}

const SECTION_PATHS: Record<string, string> = {
    'my-tasks': '/my-tasks',
    'team': '/team',
    'all-tasks': '/all-tasks',
    'projects': '/projects',
}

// Shown during bootstrap when the URL has no valid workspace and there's no
// remembered one to fall back to (multiple workspaces). Opening one records it
// as the last workspace so future visits skip the picker.
const SelectWorkspaceModal = ({ canClose, onClose }: SelectWorkspaceModalProps) => {
    const workspaces = useAppSelector(state => state.auth?.workspaces ?? [])
    const loginSection = useAppSelector(state => state.auth?.loginSection ?? 'projects')

    const sorted = [...workspaces].sort((a, b) => a.name.localeCompare(b.name))

    const handleOpen = (ws: UserWorkspace) => {
        // The workspace load persists it as the last workspace (server-side).
        window.location.href = `/${ws.workspaceUrlId}${SECTION_PATHS[loginSection] ?? '/projects'}`
    }

    return (
        <Modal onClose={onClose} canClose={canClose} maxWidth={416}>
            <div className={css.head}>
                <TextH2>Choose a workspace</TextH2>
                <TextNorm className={css.sub}>Pick which workspace to open.</TextNorm>
            </div>
            <div className={css.list}>
                {sorted.map(ws => (
                    <button key={ws.id} type="button" className={css.item} onClick={() => handleOpen(ws)}>
                        <WorkspaceAvatar name={ws.name} avatar={ws.avatar} size="medium" className={css.ws_icon} />
                        <div className={css.meta}>
                            <TextNorm className={css.name}>{ws.name}</TextNorm>
                            <span className={css.role}>{ws.role}</span>
                        </div>
                    </button>
                ))}
            </div>
        </Modal>
    )
}

export default SelectWorkspaceModal
